import React from "react";

// Chart legend entries in the profile's LegendBadge look: a 22px outline
// pill with a swatch and a bm-label-xs label. A label longer than its row
// (a squad's full name, "… share of minutes", on a 306px phone card) wraps
// inside the pill instead of running out of the card; a short one keeps
// the one-line 22px pill.
//
//   LegendItem({ swatch = "dot" | "line" | "dashed" | "tick" | "bar" | "ring", color, label, className })
//   LegendRow({ items: [{ key?, swatch, color, label }], label, className })
//   Swatch({ swatch, color, ring })   the mark alone (a table cell, a tile)
//   ArchetypeDot({ color, size })     a data colour dot with its 1px ring
//
// Swatches: "dot" a 7px dot (this player, a team win), "line" a solid
// 3px line (a rolling average or trend), "dashed" a dashed line (a season
// average), "tick" a short vertical tick (a peer median), "bar" a small
// rounded bar (a bar series), "ring" a hollow dot (a camp game). `color` is
// any CSS colour; use charts/chartTokens so it stays on the bm palette.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const Swatch = ({ swatch = "dot", color = "#2c2c2c", ring = false }) => {
  switch (swatch) {
    case "line":
      return <span aria-hidden="true" className="inline-block h-0 w-4 shrink-0 rounded-full border-t-[3px]" style={{ borderTopColor: color }} />;
    case "dashed":
      return <span aria-hidden="true" className="inline-block h-0 w-4 shrink-0 border-t-2 border-dashed" style={{ borderTopColor: color }} />;
    case "tick":
      return <span aria-hidden="true" className="inline-block h-[11px] w-[2px] shrink-0 rounded-full" style={{ background: color }} />;
    case "bar":
      return <span aria-hidden="true" className="inline-block h-[9px] w-[11px] shrink-0 rounded-[3px]" style={{ background: color }} />;
    case "ring":
      return <span aria-hidden="true" className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ border: `2px solid ${color}` }} />;
    default:
      return (
        <span
          aria-hidden="true"
          className={cx("inline-block h-[7px] w-[7px] shrink-0 rounded-full", ring && "ring-1 ring-bm-ink/20")}
          style={{ background: color }}
        />
      );
  }
};

export const ArchetypeDot = ({ color, size = 10, className = "" }) => (
  <span
    aria-hidden="true"
    className={cx("inline-block shrink-0 rounded-full ring-1 ring-bm-ink/20", className)}
    style={{ background: color, width: size, height: size }}
  />
);

// min-h + py-[3px]: one line is exactly 22px high (1 + 3 + 13 + 3 + 1),
// two lines grow the pill instead of overflowing it.
export const LegendItem = ({ swatch = "dot", color, label, className = "" }) => (
  <span
    className={cx(
      "inline-flex min-h-[22px] max-w-full items-center gap-[6px] rounded-full border border-bm-strong bg-white px-[9px] py-[3px] bm-label-xs text-bm-ink",
      className,
    )}
  >
    <Swatch swatch={swatch} color={color} />
    <span className="min-w-0 break-words">{label}</span>
  </span>
);

export const LegendRow = ({ items = [], label = "Legend", className = "" }) => {
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
    <ul role="list" aria-label={label} className={cx("flex flex-wrap gap-2", className)}>
      {shown.map((item, i) => (
        <li key={item.key || `${item.label}-${i}`} className="flex min-w-0 max-w-full">
          <LegendItem swatch={item.swatch} color={item.color} label={item.label} />
        </li>
      ))}
    </ul>
  );
};

export default LegendItem;
